import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    serviceInterest: '',
    currentCreditScore: '',
    businessStatus: '',
    fundingGoal: '',
    message: '',
    consentNonMarketing: false,
    consentMarketing: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/applications`, formData);
      
      if (response.status === 200 || response.status === 201) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          serviceInterest: '',
          currentCreditScore: '',
          businessStatus: '',
          fundingGoal: '',
          message: '',
          consentNonMarketing: false,
          consentMarketing: false
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dark-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="display-huge page-title">Contact Us</h1>
          <p className="body-large page-subtitle">
            Take the first step toward financial structure and fundability
          </p>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="application-form-section">
        <div className="form-container">
          <div className="form-intro">
            <h2 className="heading-1">Tell Us About Your Goals</h2>
            <p className="body-medium">
              Complete this application to schedule your initial assessment. A strategist will contact you within 24 hours to discuss your custom roadmap.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="application-form">
            {/* Personal Information */}
            <div className="form-section">
              <h3 className="heading-3 form-section-title">Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Service Interest */}
            <div className="form-section">
              <h3 className="heading-3 form-section-title">Service Interest</h3>
              <div className="form-group">
                <label htmlFor="serviceInterest" className="form-label">What service are you interested in? *</label>
                <select
                  id="serviceInterest"
                  name="serviceInterest"
                  value={formData.serviceInterest}
                  onChange={handleChange}
                  required
                  className="form-select"
                >
                  <option value="">Select a service...</option>
                  <option value="wealth-rebuild">Wealth Rebuild System™</option>
                  <option value="credit-optimization">Credit Optimization</option>
                  <option value="business-formation">Business Formation Package</option>
                  <option value="tradelines">Primary Tradelines</option>
                  <option value="student-loans">Student Loan Removal</option>
                  <option value="funding-readiness">90-Day Funding Readiness</option>
                  <option value="consultation">General Consultation</option>
                </select>
              </div>
            </div>

            {/* Financial Profile */}
            <div className="form-section">
              <h3 className="heading-3 form-section-title">Financial Profile</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="currentCreditScore" className="form-label">Current Credit Score Range</label>
                  <select
                    id="currentCreditScore"
                    name="currentCreditScore"
                    value={formData.currentCreditScore}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">Select range...</option>
                    <option value="below-550">Below 550</option>
                    <option value="550-600">550-600</option>
                    <option value="600-650">600-650</option>
                    <option value="650-700">650-700</option>
                    <option value="700-750">700-750</option>
                    <option value="above-750">Above 750</option>
                    <option value="unknown">Don't Know</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="businessStatus" className="form-label">Business Status</label>
                  <select
                    id="businessStatus"
                    name="businessStatus"
                    value={formData.businessStatus}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">Select status...</option>
                    <option value="no-business">No Business Yet</option>
                    <option value="planning">Planning to Start</option>
                    <option value="new-business">Business Less Than 1 Year</option>
                    <option value="established">Established Business (1+ Years)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="fundingGoal" className="form-label">Funding Goal</label>
                <select
                  id="fundingGoal"
                  name="fundingGoal"
                  value={formData.fundingGoal}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Select goal...</option>
                  <option value="10k-25k">$10,000 - $25,000</option>
                  <option value="25k-50k">$25,000 - $50,000</option>
                  <option value="50k-100k">$50,000 - $100,000</option>
                  <option value="100k-250k">$100,000 - $250,000</option>
                  <option value="250k-plus">$250,000+</option>
                  <option value="not-sure">Not Sure Yet</option>
                </select>
              </div>
            </div>

            {/* Additional Information */}
            <div className="form-section">
              <h3 className="heading-3 form-section-title">Additional Information</h3>
              <div className="form-group">
                <label htmlFor="message" className="form-label">Tell us more about your situation and goals</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className="form-textarea"
                  placeholder="Share any additional details that will help us understand your needs..."
                ></textarea>
              </div>
            </div>

            {/* Consent Checkboxes */}
            <div className="form-section consent-section">
              <div className="consent-group">
                <label className="consent-label">
                  <input
                    type="checkbox"
                    name="consentNonMarketing"
                    checked={formData.consentNonMarketing}
                    onChange={handleChange}
                    className="consent-checkbox"
                  />
                  <span className="consent-text">
                    By checking this box, I consent to receive non-marketing text messages from The Collins Academy for reaching out to customers about appointment reminders, appointment confirmations. Message frequency varies, message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                  </span>
                </label>
              </div>
              
              <div className="consent-group">
                <label className="consent-label">
                  <input
                    type="checkbox"
                    name="consentMarketing"
                    checked={formData.consentMarketing}
                    onChange={handleChange}
                    className="consent-checkbox"
                  />
                  <span className="consent-text">
                    By checking this box, I consent to receive marketing and promotional messages including special offers, discounts, new product updates among others from The Collins Academy at the phone number provided. Frequency may vary. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                  </span>
                </label>
              </div>

              <div className="legal-links">
                <Link to="/privacy-policy" className="legal-link">Privacy Policy</Link>
                <span className="legal-separator">|</span>
                <Link to="/terms-of-service" className="legal-link">Terms of Service</Link>
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-submit-section">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary form-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="form-success-message">
                  <CheckCircle2 size={24} />
                  <div>
                    <div className="success-title">Application Submitted Successfully!</div>
                    <div className="success-text">Thank you for your interest. A strategist will contact you within 24 hours.</div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="form-error-message">
                  <div className="error-title">Submission Failed</div>
                  <div className="error-text">Please try again or contact us directly at information@thecollinsacademy.com</div>
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="next-steps-section">
        <div className="section-header">
          <h2 className="display-large section-title">What Happens Next?</h2>
        </div>

        <div className="next-steps-grid">
          <div className="next-step-card">
            <div className="next-step-number">01</div>
            <h3 className="heading-3 next-step-title">Initial Contact</h3>
            <p className="body-medium next-step-text">
              A dedicated strategist will reach out within 24 hours to schedule your assessment call.
            </p>
          </div>

          <div className="next-step-card">
            <div className="next-step-number">02</div>
            <h3 className="heading-3 next-step-title">Assessment Call</h3>
            <p className="body-medium next-step-text">
              We'll review your current situation, goals, and discuss which services align with your needs.
            </p>
          </div>

          <div className="next-step-card">
            <div className="next-step-number">03</div>
            <h3 className="heading-3 next-step-title">Custom Roadmap</h3>
            <p className="body-medium next-step-text">
              Receive your personalized financial structure roadmap with timeline and investment details.
            </p>
          </div>

          <div className="next-step-card">
            <div className="next-step-number">04</div>
            <h3 className="heading-3 next-step-title">Implementation</h3>
            <p className="body-medium next-step-text">
              Once you're ready, we begin executing your plan with full strategist support.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;